import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useBlocker } from "react-router-dom"
import { useProjectStore } from "stores/projectStore"
import { Loader } from "shared/ui/Loader/Loader.component"
import EmptyState from "shared/ui/EmptyState/EmptyState.component"
import ConfirmationModal from "shared/ui/ConfimationModal/ConfimationModal.component"
import { notify } from "shared/ui/Notifications/notify"
import {
  AssistantDock,
  AssistantToggle,
  ProposalOf,
  ProposalStatus,
  selectConversation,
  useAssistantStore,
  useRegisterAssistantScreen,
} from "features/Assistant"
import { contentHash } from "features/TabSpec/model/hash"
import {
  useDashboardDocument,
  useDashboardMutations,
} from "features/Dashboard/api/useDashboardFiles"
import {
  DashboardDocument,
  normalizeDashboardDocument,
} from "features/Dashboard/types/dashboardDocument"
import { usePipelines } from "features/Dashboard/api/usePipelines"
import {
  assemblePrepScript,
  PrepPipeline,
} from "features/Dashboard/runtime/prepScript"
import {
  createDashboardStore,
  DashboardMode,
  DashboardStoreProvider,
  selectIsDirty,
  useDashboardStore,
  useDashboardStoreApi,
} from "features/Dashboard/runtime/dashboardStore"
import { useDashboardSession } from "features/Dashboard/runtime/useDashboardSession"
import {
  DashboardQueueContext,
  useDashboardQueue,
  useTileRunner,
} from "features/Dashboard/runtime/useTileRunner"
import DashboardWorkspace from "features/Dashboard/components/DashboardWorkspace.component"
import { settleLayouts } from "features/Dashboard/components/Grid/DashboardGrid.component"
import ExportModal from "features/Dashboard/components/ExportModal/ExportModal.component"
import FilterDefinitionPopover from "features/Dashboard/components/FilterBar/FilterDefinitionPopover.component"
// The --qc-* tokens the dashboard shares live in the console stylesheet.
import "styles/queryConsole.css"
import "styles/dashboard.css"

const NARROW_BREAKPOINT = 1024
/** Pause after the last preparation edit before the session restarts. */
const PREP_RESTART_DELAY_MS = 800
const NO_PIPELINES: PrepPipeline[] = []

/** The toast when a proposal's document cannot be read; nothing is loaded. */
export const INVALID_PROPOSAL_TEXT =
  "The assistant's dashboard could not be read, so nothing was loaded."
/** The toast when a proposal is a dashboard of the old format; nothing is loaded. */
/** The toast when tiles the proposal names did not survive reading; nothing is loaded. */
export const UNREADABLE_TILES_TEXT =
  "Some of the assistant's tiles could not be read, so nothing was loaded. Ask again."
export const OLD_FORMAT_PROPOSAL_TEXT =
  "The assistant's dashboard is in an older format, so nothing was loaded."
/** Why Review is off for a proposal made for another dashboard. */
export const otherDashboardText = (name: string): string =>
  `This proposal is for ${name}; open it to review`
/** Why Review is off in View mode, which has no Save. */
export const BUILD_TO_REVIEW_TEXT = "Switch to Build to review this"

/**
 * `baseHash` of a dashboard: `contentHash` (as for a tab spec), the hash of
 * its canonical JSON without the updated stamp. Worked out once per
 * document object (every edit makes a new one), since the proposal cards
 * ask on every render.
 */
const hashes = new WeakMap<DashboardDocument, string>()
export const dashboardHash = (document: DashboardDocument): string => {
  const known = hashes.get(document)
  if (known !== undefined) return known
  const hash = contentHash(document)
  hashes.set(document, hash)
  return hash
}

/** A proposal loaded into the draft, with the draft before it for its Undo. */
interface Loaded {
  before: DashboardDocument
  beforeAiTileIds: string[]
  beforePageId: string
  /**
   * The draft right after the load and what was saved then: Undo is
   * offered while both are still these (nothing edited, nothing saved).
   */
  after: DashboardDocument
  saved: string
}

/**
 * Tells the assistant this is the Dashboards screen: the open dashboard goes
 * with each turn (`baseHash` says which version was asked about), and a
 * Dashboard proposal is reviewed here. Review loads the proposed document
 * into the draft, the tiles it added or changed tagged "AI" and their page
 * shown; the dashboard is then dirty, and the user saves it as any edit, or
 * takes it back with the card's Undo while nothing else has changed. Review
 * is off for a proposal made for another dashboard and in View mode; one
 * asked about another version of the dashboard is out of date.
 */
const DashboardAssistant: React.FC = () => {
  const store = useDashboardStoreApi()
  const projectId = useDashboardStore((s) => s.projectId)
  const name = useDashboardStore((s) => s.name)
  const mode = useDashboardStore((s) => s.mode)
  // The cards ask the handlers again on an edit or a save only while a
  // dashboard proposal waits or can still be undone (not on every edit).
  const watching = useAssistantStore((s) =>
    selectConversation(projectId)(s).proposals.some(
      ({ proposal, status }) =>
        proposal.kind === "dashboard" &&
        (status === "pending" || status === "accepted")
    )
  )
  const watchedDocument = useDashboardStore((s) =>
    watching ? s.document : null
  )
  const watchedSaved = useDashboardStore((s) =>
    watching ? s.savedSnapshot : null
  )
  const revision = useMemo(
    () => ({}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [watchedDocument, watchedSaved, mode]
  )
  const loaded = useRef(new Map<string, Loaded>())

  const setStatus = (id: string, status: ProposalStatus): void =>
    useAssistantStore.getState().setProposalStatus(projectId, id, status)

  const isStale = (p: ProposalOf<"dashboard">): boolean =>
    p.name === name && p.baseHash !== dashboardHash(store.getState().document)

  const review = (p: ProposalOf<"dashboard">): void => {
    if (p.name !== name || store.getState().mode !== "build") return
    const result = normalizeDashboardDocument(p.document)
    if (!result.ok) {
      notify.toast.error(
        result.reason === "old-format"
          ? OLD_FORMAT_PROPOSAL_TEXT
          : INVALID_PROPOSAL_TEXT
      )
      return
    }
    // A tile the dashboard cannot read is dropped on reading: loading the rest
    // would show "Accepted" with the asked-for tile missing.
    const readIds = new Set(result.document.pages.flatMap((page) => page.tiles.map((t) => t.id)))
    const changed = Array.isArray(p.changedTileIds) ? p.changedTileIds : []
    if (changed.some((id) => !readIds.has(id))) {
      notify.toast.error(UNREADABLE_TILES_TEXT)
      return
    }
    if (isStale(p)) {
      setStatus(p.id, "stale")
      return
    }
    const {
      document: before,
      aiTileIds: beforeAiTileIds,
      activePageId: beforePageId,
      loadDraft,
    } = store.getState()
    // The stamps are not the assistant's to change. The tiles go where the
    // grid would put them, so showing them is not a further edit.
    const proposed = settleLayouts({
      ...result.document,
      createdAt: before.createdAt,
      updatedAt: before.updatedAt,
    })
    loadDraft(proposed, Array.isArray(p.changedTileIds) ? p.changedTileIds : [])
    const after = store.getState()
    loaded.current.set(p.id, {
      before,
      beforeAiTileIds,
      beforePageId,
      after: after.document,
      saved: after.savedSnapshot,
    })
    setStatus(p.id, "accepted")
  }

  const undoable = (p: ProposalOf<"dashboard">): Loaded | null => {
    const was = loaded.current.get(p.id)
    const now = store.getState()
    return was && now.document === was.after && now.savedSnapshot === was.saved
      ? was
      : null
  }

  useRegisterAssistantScreen({
    screen: "dashboards",
    getContext: () => {
      const current = store.getState().document
      return {
        dashboard: { name, document: current, baseHash: dashboardHash(current) },
      }
    },
    revision,
    handlers: {
      dashboard: {
        review: (p) => {
          if (p.kind === "dashboard") review(p)
        },
        // Another dashboard's proposal is not out of date here: it is blocked.
        isStale: (p) => p.kind === "dashboard" && isStale(p),
        blockedReason: (p) => {
          if (p.kind !== "dashboard") return null
          if (p.name !== name) return otherDashboardText(p.name)
          if (store.getState().mode !== "build") return BUILD_TO_REVIEW_TEXT
          return null
        },
        canUndo: (p) => p.kind === "dashboard" && undoable(p) !== null,
        undo: (p) => {
          const was = p.kind === "dashboard" ? undoable(p) : null
          if (!was) return
          const { revertTo, setActivePage } = store.getState()
          revertTo(was.before, was.beforeAiTileIds)
          // Back to the page that was shown before Review.
          if (was.before.pages.some((page) => page.id === was.beforePageId)) {
            setActivePage(was.beforePageId)
          }
          loaded.current.delete(p.id)
          // Back as before Review: it can be reviewed or rejected again.
          setStatus(p.id, "pending")
        },
      },
    },
  })
  return null
}

const useNarrow = (): boolean => {
  const [narrow, setNarrow] = useState(
    () => window.innerWidth < NARROW_BREAKPOINT
  )
  useEffect(() => {
    const onResize = (): void =>
      setNarrow(window.innerWidth < NARROW_BREAKPOINT)
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])
  return narrow
}

/** Everything that needs the store: session, runner, save, prompts. */
const DashboardInner: React.FC<{ name: string }> = ({ name }) => {
  const store = useDashboardStoreApi()
  const project = useProjectStore((s) => s.project)
  const data = useDashboardStore((s) => s.document.data)
  const prepScript = useDashboardStore((s) => s.document.prepScript)
  // Only a dashboard that applies pipelines waits for them to start.
  const applies = data.pipelines.length > 0
  const pipelinesQuery = usePipelines(
    project.id,
    project.Created_by,
    applies
  )
  const pipelines: PrepPipeline[] | null =
    pipelinesQuery.data ??
    (applies && pipelinesQuery.isLoading ? null : NO_PIPELINES)
  // The list failed and there is no earlier one to use: the preparation
  // does not run until it loads (the Data panel asks for it again).
  const pipelinesFailed =
    applies && pipelinesQuery.isError && pipelinesQuery.data === undefined
  const { restart } = useDashboardSession(pipelines, pipelinesFailed)
  const queue = useDashboardQueue()
  useTileRunner(queue)
  const { save, isSaving } = useDashboardMutations(
    project.id,
    project.Created_by
  )
  const status = useDashboardStore((s) => s.session.status)
  const sessionError = useDashboardStore((s) => s.session.error)
  const everReady = useDashboardStore((s) => s.codebook !== null)
  const [exportOpen, setExportOpen] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const narrow = useNarrow()

  // Leaving for another page with unsaved changes asks first. A new
  // ?mode= on the same dashboard keeps the draft, so it passes.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      selectIsDirty(store.getState()) &&
      currentLocation.pathname !== nextLocation.pathname
  )

  // A change to what the preparation runs (pipelines, their scripts, the
  // weight, the Advanced script), or to what keeps it from running (an
  // applied pipeline the list does not have, a list that failed to load),
  // re-runs RESET + preparation + CODEBOOK after a pause. Null while the
  // pipelines load: the session starts once they are in, so the first
  // preparation is not a change.
  const prep = useMemo(() => {
    if (pipelines === null) return null
    const { script, missing } = assemblePrepScript({
      pipelines,
      chosen: data.pipelines,
      weight: data.weight,
      advanced: prepScript,
    })
    return JSON.stringify({ script, missing, failed: pipelinesFailed })
  }, [pipelines, pipelinesFailed, data, prepScript])
  const restartRef = useRef(restart)
  useEffect(() => {
    restartRef.current = restart
  }, [restart])
  const lastPrep = useRef(prep)
  // A restart already waiting is kept while the pipelines reload, so an
  // edit made just before a reload is not lost.
  const pendingRestart = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (prep === null || lastPrep.current === prep) return
    const first = lastPrep.current === null
    lastPrep.current = prep
    if (first) return
    if (pendingRestart.current) clearTimeout(pendingRestart.current)
    pendingRestart.current = setTimeout(() => {
      pendingRestart.current = null
      void restartRef.current()
    }, PREP_RESTART_DELAY_MS)
  }, [prep])
  useEffect(
    () => () => {
      if (pendingRestart.current) clearTimeout(pendingRestart.current)
    },
    []
  )

  const saving = useRef(false)
  const onSave = useCallback(async (): Promise<void> => {
    if (saving.current) return
    saving.current = true
    const sent = store.getState().document
    try {
      await save(name, sent)
      // Snapshot what was sent, not the current draft: an edit made while
      // the save was in flight stays unsaved.
      store.getState().markSaved(sent)
      notify.success("Dashboard saved")
    } catch (e) {
      notify.error(
        "Save failed",
        e instanceof Error ? e.message : "Unknown error"
      )
    } finally {
      saving.current = false
    }
  }, [save, name, store])

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault()
        if (selectIsDirty(store.getState())) void onSave()
      }
    }
    const onUnload = (e: BeforeUnloadEvent): void => {
      if (selectIsDirty(store.getState())) {
        e.preventDefault()
        e.returnValue = ""
      }
    }
    window.addEventListener("keydown", onKey)
    window.addEventListener("beforeunload", onUnload)
    return () => {
      window.removeEventListener("keydown", onKey)
      window.removeEventListener("beforeunload", onUnload)
    }
  }, [onSave, store])

  // Without a first successful start there is nothing to show. A failed
  // restart keeps the workspace and reports over the canvas instead.
  const failedToOpen = status === "error" && !everReady

  return (
    <>
      {failedToOpen ? (
        <EmptyState
          message="Could not open this dashboard"
          subtitle={sessionError ?? "Unknown error"}
        />
      ) : (
        <>
          {/* The tiles send the check of a traced cell on the same queue as their runs. */}
          <DashboardQueueContext.Provider value={queue}>
            <DashboardWorkspace
              onSave={() => void onSave()}
              onExport={() => setExportOpen(true)}
              onOpenFilters={() => setFiltersOpen(true)}
              isSaving={isSaving}
              onRegenerateCodebook={() => void restart()}
              isCodebookLoading={status === "starting"}
              narrow={narrow}
              headerActions={<AssistantToggle />}
            />
          </DashboardQueueContext.Provider>
          <ExportModal
            open={exportOpen}
            onClose={() => setExportOpen(false)}
            queue={queue}
          />
          <FilterDefinitionPopover
            open={filtersOpen}
            onClose={() => setFiltersOpen(false)}
          />
        </>
      )}
      <ConfirmationModal
        title="Leave without saving?"
        isVisible={blocker.state === "blocked"}
        message="Your changes to this dashboard have not been saved."
        cancel={() => blocker.reset?.()}
        confirm={() => blocker.proceed?.()}
        confirmButtonText="Leave"
        confirmButtonType="danger"
      />
    </>
  )
}

/**
 * One loaded dashboard. The store is created once per mount (the container
 * keys this by project and name): a save refreshes the query cache, but the
 * draft lives on in the store.
 */
const LoadedDashboard: React.FC<{
  initialDocument: DashboardDocument
  mode: DashboardMode
  name: string
  projectId: string
}> = ({ initialDocument, mode, name, projectId }) => {
  const [store] = useState(() =>
    createDashboardStore({ document: initialDocument, mode, projectId, name })
  )
  // A new ?mode= on the same dashboard switches mode in place.
  const lastMode = useRef(mode)
  useEffect(() => {
    if (lastMode.current === mode) return
    lastMode.current = mode
    store.getState().setMode(mode)
  }, [mode, store])

  return (
    <DashboardStoreProvider store={store}>
      <DashboardAssistant />
      <DashboardInner name={name} />
    </DashboardStoreProvider>
  )
}

/** Loads the dashboard file `name` of the current project and opens it in `mode`. */
const DashboardContainer: React.FC<{ name: string; mode: DashboardMode }> = ({
  name,
  mode,
}) => {
  const project = useProjectStore((s) => s.project)
  const doc = useDashboardDocument(project.id, project.Created_by, name)

  let content: React.ReactNode
  if (doc.data?.ok) {
    content = (
      <LoadedDashboard
        key={`${project.id}/${name}`}
        initialDocument={doc.data.document}
        mode={mode}
        name={name}
        projectId={project.id}
      />
    )
  } else if (doc.data?.reason === "old-format") {
    content = (
      <EmptyState
        message="This dashboard was saved in an older format and cannot be opened here."
        subtitle="Create a new dashboard from the Dashboards tab."
      />
    )
  } else if (doc.data) {
    content = (
      <EmptyState
        message="This dashboard file could not be read"
        subtitle="Create a new dashboard from the Dashboards tab."
      />
    )
  } else if (doc.isFetching) {
    content = (
      <div className="flex items-center justify-center h-full">
        <Loader fontSize={48} />
      </div>
    )
  } else {
    // The load error carries the backend text (for example "Error 1019: ...").
    const reason = doc.error instanceof Error ? doc.error.message : ""
    content = (
      <EmptyState
        message="Dashboard not found"
        subtitle={reason || `"${name}" could not be loaded.`}
      />
    )
  }

  // The assistant's panel docks on the right of the page, beside the dashboard.
  return (
    <AssistantDock projectId={project.id} owner={project.Created_by}>
      <div className="h-full p-3">{content}</div>
    </AssistantDock>
  )
}

export default DashboardContainer
